import type { TemplatePortfolioDraft } from "@adpilot/shared";
import { IconCheck } from "@tabler/icons-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export const TEMPLATE_PORTFOLIO_STEPS = [
  "basics",
  "keywords",
  "preview",
  "execute",
  "enable",
] as const;

/** 1-based: ① 基本信息 … ⑤ 开启. */
export type TemplatePortfolioStep = 1 | 2 | 3 | 4 | 5;

/** Props shared by steps ② 关键词 and ③ 结构预览 (both edit the draft). */
export interface TemplatePortfolioDraftStepProps {
  /** The draft's shop; every API call sends it as X-Shop-Id. */
  shopId: string;
  /** Latest draft (TanStack Query cache; draft write hooks keep it current). */
  draft: TemplatePortfolioDraft;
  onBack: () => void;
}

/**
 * Page frame (Paper TP-01 / TP-09 / TP-10): 88px header with title + 5-step indicator,
 * a scrolling body (px-6 pt-6), and the step's own sticky {@link TemplatePortfolioFooter}.
 * The body has no gap: each step owns its vertical rhythm (Paper uses pb-6 before the footer).
 */
export function TemplatePortfolioShell({
  step,
  children,
}: {
  step: TemplatePortfolioStep;
  children: React.ReactNode;
}) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <div
      data-page-scroll-owner="template-portfolio"
      className="flex min-h-0 min-w-[960px] flex-1 flex-col"
    >
      <header className="flex h-[88px] shrink-0 items-center justify-between gap-6 border-b border-line px-6">
        <h1 className="text-lg font-semibold text-ink">{t("title")}</h1>
        <TemplatePortfolioStepIndicator step={step} />
      </header>
      <div className="paper-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-6 pt-6">
        {children}
      </div>
    </div>
  );
}

function TemplatePortfolioStepIndicator({ step }: { step: TemplatePortfolioStep }) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <ol className="flex items-center gap-3 text-xs">
      {TEMPLATE_PORTFOLIO_STEPS.map((key, index) => {
        const number = index + 1;
        const done = number < step;
        const current = number === step;
        return (
          <Fragment key={key}>
            {index > 0 ? <li aria-hidden className="h-px w-5 shrink-0 bg-line" /> : null}
            <li aria-current={current ? "step" : undefined} className="flex items-center gap-2">
              <span
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] leading-[14px]",
                  current ? "bg-ink text-surface" : "bg-subtle text-muted-foreground",
                )}
              >
                {done ? <IconCheck className="size-3 text-ink" stroke={2} aria-hidden /> : number}
              </span>
              <span className={cn(current ? "font-medium text-ink" : "text-muted-foreground")}>
                {t(`steps.${key}`)}
              </span>
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
}

/**
 * Sticky 72px bottom bar. `hint` is the left side — a short text (TP-10) or a button
 * such as 取消 (TP-01); `children` are the right-side buttons.
 */
export function TemplatePortfolioFooter({
  hint,
  children,
}: {
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="sticky bottom-0 -mx-6 mt-auto flex h-[72px] shrink-0 items-center justify-between gap-6 border-t border-line bg-table-footer px-6 py-4">
      <div className="flex min-w-0 items-center *:text-xs">{hint}</div>
      {/* Paper footer buttons use 12px text; Button defaults to 14px. */}
      <div className="flex shrink-0 items-center gap-2 *:text-xs">{children}</div>
    </div>
  );
}
